import { cn } from '../../lib/cn.js'

export interface PhoneNumberProps {
  /** E.164 as stored — `+14165550123`. */
  value: string | null | undefined
  /** Falls back to this when there is no number. */
  empty?: string
  /** Makes it a `tel:` link. On by default: the desk is used to ring people. */
  callable?: boolean
  className?: string
}

/**
 * A phone number, grouped so a person can read it out loud.
 *
 * The API stores and returns E.164, which is the right thing to store and the
 * wrong thing to show: `+14165550123` is fifteen characters somebody has to
 * decode before they can dial it. Printed with gaps, it is a phone number again.
 *
 * Only North American numbers are regrouped, because their shape is known.
 * Anything else is shown exactly as stored rather than grouped wrongly — a badly
 * spaced foreign number is harder to read than an unspaced one, not easier.
 */
export function PhoneNumber({ value, empty = '–', callable = true, className }: PhoneNumberProps) {
  if (!value) return <span className={cn('text-ink-muted', className)}>{empty}</span>

  const digits = value.replace(/[^\d+]/g, '')
  const na = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(digits)
  const text = na ? `+1 ${na[1]} ${na[2]}-${na[3]}` : value

  const body = <span className="tabular-nums">{text}</span>
  if (!callable) return <span className={className}>{body}</span>
  return (
    <a
      href={`tel:${digits}`}
      className={cn('text-ink hover:text-coral-fill hover:underline', className)}
    >
      {body}
    </a>
  )
}
